import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@platewell/schema';
import { useEffect, useState, type FormEvent } from 'react';
import {
  fetchInvitePreview,
  requestPasswordReset,
  signIn,
  signUp,
  type InvitePreview,
  type Session,
} from './lib/api.js';

type Mode = 'sign-in' | 'register' | 'forgot';

const AUTH_PATHS: Record<Mode, string> = {
  'sign-in': '/login',
  register: '/register',
  forgot: '/forgot-password',
};

/** The marketing site links straight at /register, so the path picks the form, not the toggle. */
function modeForPath(pathname: string): Mode {
  if (pathname === AUTH_PATHS.register) return 'register';
  if (pathname === AUTH_PATHS.forgot) return 'forgot';
  return 'sign-in';
}

/** The token from an `/invite/<token>` link, or null on any other path. */
export function inviteTokenForPath(pathname: string): string | null {
  const match = /^\/invite\/([^/]+)\/?$/.exec(pathname);
  return match == null ? null : decodeURIComponent(match[1]!);
}

/**
 * Email + password sign-in, registration, and forgot-password request.
 *
 * Password reset completion lives on `/reset-password` so the emailed link can land on a
 * dedicated form whether the request started on web or mobile.
 *
 * Given an invite token, the screen greets the invitee instead: it opens on signup with the
 * invited address filled in, and never rewrites the URL, so once the session exists the
 * router is still on `/invite/<token>` and accepts the invite by itself.
 */
export function SignIn({
  onSignedIn,
  inviteToken = null,
}: {
  onSignedIn: (session: Session) => void;
  inviteToken?: string | null;
}) {
  const [mode, setMode] = useState<Mode>(() =>
    inviteToken != null ? 'register' : modeForPath(window.location.pathname),
  );
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inviteToken == null) return;
    let cancelled = false;
    void fetchInvitePreview(inviteToken)
      .then((preview) => {
        if (cancelled) return;
        setInvite(preview);
        // Only fills a blank field: someone quick enough to have started typing keeps theirs.
        setEmail((current) => (current === '' ? preview.email : current));
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setInviteError(cause instanceof Error ? cause.message : 'Invite is not valid');
      });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setSent(false);
    setPassword('');
    setConfirm('');
    // Replaced rather than pushed: this is one screen changing shape, so Back should leave the
    // auth screen instead of walking back through the modes. The guard leaves someone who
    // arrived at bare "/" there until they actually switch.
    // An invite link keeps its path whatever the form shows, or signing in would lose it.
    if (inviteToken == null && modeForPath(window.location.pathname) !== next) {
      window.history.replaceState(null, '', AUTH_PATHS[next]);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'sign-in') {
        onSignedIn(await signIn(email.trim(), password));
        return;
      }
      if (mode === 'register') {
        if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
          setError(`Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`);
          return;
        }
        if (password !== confirm) {
          setError('Passwords do not match');
          return;
        }
        onSignedIn(await signUp(name.trim(), email.trim(), password, inviteToken));
        return;
      }
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    email.trim() !== '' &&
    (mode === 'forgot'
      ? true
      : password.length > 0 && (mode === 'sign-in' || (name.trim() !== '' && confirm.length > 0)));

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(event) => void submit(event)}>
        <div className="brand">
          <img className="brand-mark" src="/logo-256.png" alt="" width={128} height={128} /> Platewell
        </div>

        {mode === 'forgot' && sent ? (
          <>
            <p className="hint">
              If an account exists for <strong>{email}</strong>, we sent a reset link. Open it on
              any device to choose a new password.
            </p>
            <button type="button" className="btn primary" onClick={() => switchMode('sign-in')}>
              Back to sign in
            </button>
          </>
        ) : (
          <>
            {inviteToken != null && mode !== 'forgot' && (
              <InviteGreeting invite={invite} error={inviteError} mode={mode} />
            )}

            {(inviteToken == null || mode === 'forgot') && (
              <p className="hint">
                {mode === 'sign-in' &&
                  'Your recipe box, on every device, with or without a signal.'}
                {mode === 'register' && 'Create an account to keep recipes across every device.'}
                {mode === 'forgot' && 'Enter your email and we will send a password reset link.'}
              </p>
            )}

            {mode === 'register' && (
              <div className="field">
                <label className="label" htmlFor="name">
                  Name
                </label>
                <input
                  id="name"
                  className="input"
                  type="text"
                  required
                  // The field only mounts in register mode, so React focuses it both for
                  // someone arriving at /register and for someone switching to it here.
                  autoFocus
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Your name"
                />
              </div>
            )}

            <div className="field">
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="input"
                type="email"
                required
                autoComplete="email"
                spellCheck={false}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </div>

            {mode !== 'forgot' && (
              <div className="field">
                <label className="label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  required
                  minLength={mode === 'register' ? MIN_PASSWORD_LENGTH : undefined}
                  maxLength={mode === 'register' ? MAX_PASSWORD_LENGTH : undefined}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                />
                {mode === 'register' && (
                  <p className="hint">
                    Password must be between {MIN_PASSWORD_LENGTH} and {MAX_PASSWORD_LENGTH}{' '}
                    characters.
                  </p>
                )}
              </div>
            )}

            {mode === 'register' && (
              <div className="field">
                <label className="label" htmlFor="confirm">
                  Confirm password
                </label>
                <input
                  id="confirm"
                  className="input"
                  type="password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  maxLength={MAX_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  placeholder="••••••••"
                />
              </div>
            )}

            <button className="btn primary" type="submit" disabled={busy || !canSubmit}>
              {busy
                ? mode === 'forgot'
                  ? 'Sending…'
                  : mode === 'register'
                    ? 'Creating…'
                    : 'Signing in…'
                : mode === 'forgot'
                  ? 'Send reset link'
                  : mode === 'register'
                    ? 'Create account'
                    : 'Sign in'}
            </button>

            <div className="auth-links">
              {mode === 'sign-in' && (
                <>
                  <button type="button" className="btn ghost" onClick={() => switchMode('register')}>
                    Create an account
                  </button>
                  <button type="button" className="btn ghost" onClick={() => switchMode('forgot')}>
                    Forgot password?
                  </button>
                </>
              )}
              {mode === 'register' && (
                <button type="button" className="btn ghost" onClick={() => switchMode('sign-in')}>
                  Already have an account? Sign in
                </button>
              )}
              {mode === 'forgot' && (
                <button type="button" className="btn ghost" onClick={() => switchMode('sign-in')}>
                  Back to sign in
                </button>
              )}
            </div>
          </>
        )}

        {error != null && <p className="error-text">{error}</p>}
      </form>
    </div>
  );
}

function InviteGreeting({
  invite,
  error,
  mode,
}: {
  invite: InvitePreview | null;
  error: string | null;
  mode: Mode;
}) {
  if (error != null) {
    return (
      <p className="hint">
        That invite did not work: {error} You can still{' '}
        {mode === 'register' ? 'create an account' : 'sign in'}.
      </p>
    );
  }
  // The form is usable before the preview arrives; only the greeting waits on it.
  if (invite == null) return <p className="hint">Checking your invite…</p>;

  const inviter = invite.invitedByName.trim();
  return (
    <>
      <h1 className="auth-title">You're invited</h1>
      <p className="hint">
        {inviter === '' ? 'You have been invited' : `${inviter} invited you`} to share the{' '}
        <strong>{invite.householdName}</strong> recipe book.{' '}
        {mode === 'register'
          ? 'Choose a password to create your account and join.'
          : 'Sign in to join it.'}
      </p>
    </>
  );
}
